/**
 * ReceiptBuilder — pure text + ESC/POS byte builder for a receipt.
 *
 * Driven by a ReceiptConfig (see receiptConfig.ts): the `preset` picks the base
 * layout (standard / compact / detailed) and the individual toggles decide which
 * fields print. No I/O, no React, no Node APIs.
 *
 * Column widths adapt to the paper's charsPerLine, so the same builder serves
 * 58 / 80 / 112mm. The 80mm Standard layout with default toggles is intentionally
 * byte-identical to the previous hard-coded receipt.
 */

import { ReceiptBuildOutput, ReceiptData, ReceiptLineItem } from '../contracts/ReceiptContracts';
import { PrintOptions } from '../contracts/PrintTypes';
import { centerText, hr, padLeft, padRight, twoCol } from './LineFormatter';
import { escPos } from './EscPosCommands';
import { formatInr, formatQty } from './NumberFormatters';
import { getReceiptProfile } from './ReceiptProfiles';
import { ReceiptConfig, defaultReceiptConfig } from './receiptConfig';

// Re-exported for callers that only need preset helpers.
export type { ReceiptConfig, ReceiptPreset } from './receiptConfig';
export { RECEIPT_PRESETS, asReceiptPreset, defaultReceiptConfig } from './receiptConfig';

const POWERED_BY = 'Powered by BigxBook';

/**
 * Quantity paired with its unit — "2.0 kg", "1.0 pcs".
 *
 * Rule 46(f) of the CGST Rules requires a tax invoice for goods to state the
 * quantity *and* its unit, so on a thermal receipt this pairing is statutory,
 * not decoration.
 */
const formatQtyUnit = (qty: number, unit?: string): string =>
  unit && unit.trim() ? `${formatQty(qty)} ${unit.trim().toLowerCase()}` : formatQty(qty);

/**
 * Item-table columns, adapted to the paper *and* to the data.
 *
 * The unit has to appear somewhere, but it cannot simply be appended to the qty
 * column: `padLeft` truncates, so "1.5 kg" in a 4-wide column silently becomes
 * "1.5 " — worse than omitting it. And `formatQty` always emits at least three
 * characters ("1.0"), so 58mm's historical 4-wide column has no room at all.
 *
 * So the qty column grows to fit the widest quantity+unit actually being
 * printed, bounded by a floor on the name column — a receipt that fits the unit
 * by mangling product names is a bad trade. When the unit will not fit,
 * `unitInline` is false and the caller prints it on the item's meta line
 * instead. Either way it is never dropped.
 *
 * The name floors are 10 on 58mm (unchanged) and 16 on 80mm/112mm, two fewer
 * than the historical 18. That two-character concession is what lets 80mm carry
 * "1.0 pcs" (7 chars) in the column where a cashier actually reads it, rather
 * than pushing every line onto a second row. 58mm has no such room — 32 chars
 * minus a 10-char name leaves 4 — so it uses the meta line.
 */
interface Columns {
  name: number;
  qty: number;
  rate: number;
  amount: number;
  /** True when the qty column is wide enough to hold quantity + unit. */
  unitInline: boolean;
}
const columns = (w: number, items: readonly ReceiptLineItem[] = []): Columns => {
  const narrow = w <= 40;
  const baseQty = narrow ? 4 : 6;
  const rate = narrow ? 8 : 10;
  const amount = narrow ? 10 : 14;
  // Name-column floor. 58mm keeps its historical 10; wide paper gives up two
  // of its 18 so the unit fits in the qty column (see the note above).
  const nameMin = narrow ? 10 : 16;

  const widest = items.reduce(
    (max, it) => Math.max(max, formatQtyUnit(it.qty, it.unit).length),
    0,
  );
  const room = Math.max(baseQty, w - nameMin - rate - amount);
  const qty = Math.min(Math.max(baseQty, widest), room);

  return {
    qty,
    rate,
    amount,
    name: Math.max(8, w - qty - rate - amount),
    unitInline: widest > 0 && widest <= qty,
  };
};

const headerRow = (c: Columns): string =>
  padRight('Item', c.name) + padLeft('Qty', c.qty) + padLeft('Rate', c.rate) + padLeft('Amount', c.amount);

const itemRow = (name: string, qty: string, rate: string, amount: string, c: Columns): string =>
  padRight(name, c.name) + padLeft(qty, c.qty) + padLeft(rate, c.rate) + padLeft(amount, c.amount);

/**
 * The footer thank-you line: a caller-supplied contextual line (e.g. a credit
 * note annotation) wins; otherwise the store's configured message.
 */
const resolveFooterMessage = (data: ReceiptData, cfg: ReceiptConfig): string =>
  (data.footerLine1 && data.footerLine1.trim()) || cfg.footerMessage.trim();

/** Footer shared by standard/detailed: custom message, optional terms, powered-by. */
const appendFooter = (lines: string[], data: ReceiptData, cfg: ReceiptConfig, w: number): void => {
  const msg = resolveFooterMessage(data, cfg);
  if (msg) lines.push(centerText(msg, w));
  if (cfg.showTerms && cfg.terms.trim()) {
    lines.push(hr(w, '-'));
    cfg.terms
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((t, i) => lines.push(`${i + 1}. ${t}`));
  }
  if (cfg.showPoweredBy) lines.push(centerText(POWERED_BY, w));
  lines.push(hr(w, '='));
};

// ── standard (classic, itemised) ─────────────────────────────────────
const standardLines = (data: ReceiptData, cfg: ReceiptConfig, w: number): string[] => {
  const c = columns(w, data.items);
  const lines: string[] = [];
  lines.push(hr(w, '='));
  lines.push(centerText(data.storeName, w));
  if (cfg.headerTagline.trim()) lines.push(centerText(cfg.headerTagline.trim(), w));
  if (cfg.showAddress && data.addressLine) lines.push(centerText(data.addressLine, w));
  if (cfg.showGstin && data.gstin) lines.push(centerText(`GSTIN: ${data.gstin}`, w));
  if (cfg.showPhone && data.phone) lines.push(centerText(`Ph: ${data.phone}`, w));
  lines.push(hr(w, '='));

  // The invoice number is never cut to make room for the date: a long number
  // (INV-01000001) and a long date overflow 48 columns, and twoCol truncates
  // the left side. If both do not fit, they take a line each.
  const invoiceLabel = `Invoice: ${data.invoiceNumber}`;
  const dateLabel = `Date: ${data.invoiceDate}`;
  if (invoiceLabel.length + 1 + dateLabel.length <= w) {
    lines.push(twoCol(invoiceLabel, dateLabel, w));
  } else {
    lines.push(invoiceLabel);
    lines.push(dateLabel);
  }
  if (cfg.showCustomer) lines.push(`Customer: ${data.customerName || 'Walk-in'}`);
  lines.push(hr(w, '-'));

  lines.push(headerRow(c));
  lines.push(hr(w, '-'));

  data.items.forEach((item, i) => {
    lines.push(
      itemRow(
        item.name,
        c.unitInline ? formatQtyUnit(item.qty, item.unit) : formatQty(item.qty),
        formatInr(item.rate),
        formatInr(item.amount),
        c,
      ),
    );
    // Narrow paper cannot fit the unit in the qty column. It is a statutory
    // field, so it moves to its own line rather than being dropped.
    if (!c.unitInline && item.unit && item.unit.trim()) {
      lines.push(` Unit: ${item.unit.trim().toLowerCase()}`);
    }
    if (cfg.showHsn && item.hsn) lines.push(` HSN: ${item.hsn}`);
    if (item.discountPct && item.discountPct > 0) lines.push(` Disc: ${item.discountPct}%`);
    // Not after the last one — the block already closes with a rule below, and
    // two in a row just wastes a line.
    if (cfg.showItemSeparators && i < data.items.length - 1) lines.push(hr(w, '-'));
  });

  lines.push(hr(w, '-'));
  lines.push(twoCol('Subtotal:', formatInr(data.subtotal), w));
  if (data.discount && data.discount > 0) {
    lines.push(twoCol('Discount:', formatInr(-Math.abs(data.discount)), w));
  }
  if (cfg.showTaxBreakup) {
    data.taxes.forEach((tax) => lines.push(twoCol(`${tax.label}:`, formatInr(tax.amount), w)));
  }
  lines.push(hr(w, '='));
  lines.push(twoCol('TOTAL:', formatInr(data.total), w));
  lines.push(hr(w, '='));

  if (data.payment) {
    const paid = formatInr(data.payment.paid);
    const change = typeof data.payment.change === 'number' ? ` Change: ${formatInr(data.payment.change)}` : '';
    lines.push(` Paid: ${data.payment.method} ${paid}${change}`);
    lines.push(hr(w, '-'));
  }

  appendFooter(lines, data, cfg, w);
  return lines;
};

// ── compact (paper-saving) ───────────────────────────────────────────
const compactLines = (data: ReceiptData, cfg: ReceiptConfig, w: number): string[] => {
  const lines: string[] = [];
  lines.push(centerText(data.storeName, w));
  if (cfg.headerTagline.trim()) lines.push(centerText(cfg.headerTagline.trim(), w));
  if (cfg.showPhone && data.phone) lines.push(centerText(`Ph: ${data.phone}`, w));
  lines.push(twoCol(data.invoiceNumber, data.invoiceDate, w));
  if (cfg.showCustomer && data.customerName) lines.push(`Customer: ${data.customerName}`);
  lines.push(hr(w, '-'));

  data.items.forEach((item, i) => {
    // Unit sits immediately after the quantity, before the name: `twoCol`
    // truncates from the right, so the statutory field survives on narrow
    // paper while a long product name is the thing that gets clipped.
    lines.push(twoCol(`${formatQtyUnit(item.qty, item.unit)} x ${item.name}`, formatInr(item.amount), w));
    // Off by default in this preset, which exists to use less paper — but
    // honoured if a shop turns it on.
    if (cfg.showItemSeparators && i < data.items.length - 1) lines.push(hr(w, '-'));
  });

  lines.push(hr(w, '-'));
  if (data.discount && data.discount > 0) {
    lines.push(twoCol('Discount', formatInr(-Math.abs(data.discount)), w));
  }
  if (cfg.showTaxBreakup) {
    data.taxes.forEach((tax) => lines.push(twoCol(tax.label, formatInr(tax.amount), w)));
  }
  lines.push(twoCol('TOTAL', formatInr(data.total), w));
  if (data.payment) {
    lines.push(twoCol(`Paid (${data.payment.method})`, formatInr(data.payment.paid), w));
    if (typeof data.payment.change === 'number' && data.payment.change > 0) {
      lines.push(twoCol('Change', formatInr(data.payment.change), w));
    }
  }
  const fm = resolveFooterMessage(data, cfg);
  if (fm) lines.push(centerText(fm, w));
  if (cfg.showTerms && cfg.terms.trim()) {
    cfg.terms
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((t) => lines.push(centerText(t, w)));
  }
  if (cfg.showPoweredBy) lines.push(centerText(POWERED_BY, w));
  return lines;
};

// ── detailed (formal tax invoice) ────────────────────────────────────
const detailedLines = (data: ReceiptData, cfg: ReceiptConfig, w: number): string[] => {
  const c = columns(w, data.items);
  const lines: string[] = [];
  lines.push(hr(w, '='));
  lines.push(centerText('TAX INVOICE', w));
  lines.push(hr(w, '='));
  lines.push(centerText(data.storeName, w));
  if (cfg.headerTagline.trim()) lines.push(centerText(cfg.headerTagline.trim(), w));
  if (cfg.showAddress && data.addressLine) lines.push(centerText(data.addressLine, w));
  if (cfg.showGstin && data.gstin) lines.push(centerText(`GSTIN: ${data.gstin}`, w));
  if (cfg.showPhone && data.phone) lines.push(centerText(`Ph: ${data.phone}`, w));
  lines.push(hr(w, '='));

  lines.push(`Invoice : ${data.invoiceNumber}`);
  lines.push(`Date    : ${data.invoiceDate}`);
  if (cfg.showCustomer) lines.push(`Customer: ${data.customerName || 'Walk-in'}`);
  lines.push(hr(w, '-'));

  lines.push(headerRow(c));
  lines.push(hr(w, '-'));

  data.items.forEach((item, i) => {
    lines.push(
      itemRow(
        item.name,
        c.unitInline ? formatQtyUnit(item.qty, item.unit) : formatQty(item.qty),
        formatInr(item.rate),
        formatInr(item.amount),
        c,
      ),
    );
    const meta: string[] = [];
    // As in the standard layout: on narrow paper the unit rides the meta line.
    if (!c.unitInline && item.unit && item.unit.trim()) {
      meta.push(`Unit ${item.unit.trim().toLowerCase()}`);
    }
    if (cfg.showHsn && item.hsn) meta.push(`HSN ${item.hsn}`);
    if (item.discountPct && item.discountPct > 0) meta.push(`Disc ${item.discountPct}%`);
    if (meta.length) lines.push(`  ${meta.join('   ')}`);
    if (cfg.showItemSeparators && i < data.items.length - 1) lines.push(hr(w, '-'));
  });

  lines.push(hr(w, '-'));
  lines.push(twoCol('Subtotal', formatInr(data.subtotal), w));
  if (data.discount && data.discount > 0) {
    lines.push(twoCol('Discount', formatInr(-Math.abs(data.discount)), w));
  }
  if (cfg.showTaxBreakup && data.taxes.length > 0) {
    lines.push(centerText('- Tax Summary -', w));
    data.taxes.forEach((tax) => lines.push(twoCol(tax.label, formatInr(tax.amount), w)));
    const taxTotal = data.taxes.reduce((sum, t) => sum + t.amount, 0);
    lines.push(twoCol('Total Tax', formatInr(taxTotal), w));
  }
  lines.push(hr(w, '='));
  lines.push(twoCol('GRAND TOTAL', formatInr(data.total), w));
  lines.push(hr(w, '='));

  if (data.payment) {
    const change = typeof data.payment.change === 'number' ? ` | Change: ${formatInr(data.payment.change)}` : '';
    lines.push(`Paid: ${data.payment.method} ${formatInr(data.payment.paid)}${change}`);
    lines.push(hr(w, '-'));
  }

  const fm = resolveFooterMessage(data, cfg);
  if (fm) lines.push(centerText(fm, w));
  lines.push(centerText('Computer-generated invoice', w));
  if (cfg.showTerms && cfg.terms.trim()) {
    lines.push(hr(w, '-'));
    cfg.terms
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((t, i) => lines.push(`${i + 1}. ${t}`));
  }
  if (cfg.showPoweredBy) lines.push(centerText(POWERED_BY, w));
  lines.push(hr(w, '='));
  return lines;
};

const buildTextLines = (data: ReceiptData, options: PrintOptions, cfg: ReceiptConfig): string[] => {
  const w = getReceiptProfile(options.paperWidth).charsPerLine;
  switch (cfg.preset) {
    case 'compact':
      return compactLines(data, cfg, w);
    case 'detailed':
      return detailedLines(data, cfg, w);
    default:
      return standardLines(data, cfg, w);
  }
};

export const buildReceipt = (
  data: ReceiptData,
  options: PrintOptions,
  config: ReceiptConfig = defaultReceiptConfig(),
  /** Pre-built ESC/POS raster (GS v 0) for the store logo, printed centered
   *  at the very top. Omit for no logo. Never appears in `text`. */
  logoRaster?: Uint8Array,
): ReceiptBuildOutput => {
  const lines = buildTextLines(data, options, config);
  const text = `${lines.join('\n')}\n`;

  const logoBlock =
    logoRaster && logoRaster.length > 0
      ? [escPos.alignCenter(), logoRaster, escPos.feed(1)]
      : [];

  const payload = escPos.concat(
    escPos.init(),
    ...logoBlock,
    escPos.alignLeft(),
    ...lines.map((line) => escPos.textLine(line)),
    escPos.feed(3),
    options.autoCut === false ? new Uint8Array() : escPos.cut(),
  );

  return { text, bytes: payload };
};
